import { useState, useCallback } from 'react';
import { Link, useParams } from 'react-router';
import { Linkedin, Facebook, Link2, Check } from 'lucide-react';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { JsonLd, blogPostingJsonLd, breadcrumbJsonLd } from '@/components/JsonLd';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { blogPosts, type BlogSection } from '@/data/blog-posts';
import { resolveHref } from '@/lib/site-config';
import { cn, formatBlogDate } from '@/lib/utils';

// Minimal inline-link support for blog prose: turns `[label](/path)` into a
// real internal <Link> (or an external <a> for absolute URLs). Stray brackets
// that don't form the full pattern are left as plain text. Enables contextual
// internal links from posts into the marketing cluster.
const INLINE_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

function renderInline(text: string): React.ReactNode {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE_LINK_RE)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));
    const [, label, href] = match;
    const cls = 'text-primary-600 dark:text-primary-400 hover:underline font-medium';
    if (href.startsWith('/')) {
      nodes.push(<Link key={key++} to={resolveHref(href)} className={cls}>{label}</Link>);
    } else {
      nodes.push(<a key={key++} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{label}</a>);
    }
    lastIndex = index + match[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes.length === 1 ? nodes[0] : nodes;
}

const categoryColors: Record<string, string> = {
  Engineering: 'bg-blue-500/10 text-blue-400 border border-blue-500/20',
  Product: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  Guides: 'bg-amber-500/10 text-amber-400 border border-amber-500/20',
};

function AuthorAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('');
  return (
    <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-sm font-bold text-white shrink-0">
      {initials}
    </div>
  );
}

function CodeBlock({
  language,
  title,
  content,
}: {
  language: string;
  title?: string;
  content: string;
}) {
  return (
    <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            {title}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500 uppercase">
            {language}
          </span>
        </div>
      )}
      <pre className="p-4 bg-gray-950 text-gray-100 text-sm overflow-x-auto font-mono leading-relaxed">
        <code>{content.trim()}</code>
      </pre>
    </div>
  );
}

function ArticleSection({ section }: { section: BlogSection }) {
  return (
    <div className="mb-8">
      {section.heading && (
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
          {section.heading}
        </h2>
      )}
      {section.paragraphs.map((p, i) => (
        <p
          key={i}
          className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4 text-[15px]"
        >
          {renderInline(p)}
        </p>
      ))}
      {section.code && (
        <CodeBlock
          language={section.code.language}
          title={section.code.title}
          content={section.code.content}
        />
      )}
    </div>
  );
}

/* ---- Social share buttons ---- */

const shareButtonBase =
  'inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors';

function XIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function ShareButtons({ title, slug }: { title: string; slug: string }) {
  const [copied, setCopied] = useState(false);
  const url = `https://logpulse.io/blog/${slug}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [url]);

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Share</span>

      {/* X (Twitter) */}
      <a
        href={`https://x.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          shareButtonBase,
          'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400',
          'hover:border-gray-900 hover:text-gray-900 dark:hover:border-white dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800',
        )}
        aria-label="Share on X"
      >
        <XIcon className="h-3.5 w-3.5" />
      </a>

      {/* LinkedIn */}
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          shareButtonBase,
          'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400',
          'hover:border-[#0A66C2] hover:text-[#0A66C2] hover:bg-blue-50 dark:hover:bg-[#0A66C2]/10',
        )}
        aria-label="Share on LinkedIn"
      >
        <Linkedin className="h-4 w-4" />
      </a>

      {/* Facebook */}
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          shareButtonBase,
          'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400',
          'hover:border-[#1877F2] hover:text-[#1877F2] hover:bg-blue-50 dark:hover:bg-[#1877F2]/10',
        )}
        aria-label="Share on Facebook"
      >
        <Facebook className="h-4 w-4" />
      </a>

      {/* Copy link */}
      <button
        onClick={copyLink}
        className={cn(
          shareButtonBase,
          copied
            ? 'border-emerald-300 dark:border-emerald-600 text-emerald-500 bg-emerald-50 dark:bg-emerald-500/10'
            : 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-primary-300 hover:text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-500/10',
        )}
        aria-label={copied ? 'Link copied' : 'Copy link'}
      >
        {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = blogPosts.find((p) => p.slug === slug);
  useDocumentMeta({ title: post ? `${post.title}: LogPulse Blog` : 'Post Not Found: LogPulse Blog', description: post?.excerpt ?? 'The blog post you are looking for does not exist.' });

  useDocumentHead(
    post
      ? {
          title: post.title,
          description: post.excerpt,
          url: `/blog/${post.slug}`,
          type: 'article',
          publishedTime: new Date(post.date).toISOString(),
          author: post.author,
          section: post.category,
        }
      : {
          title: 'Post not found',
          description: 'The blog post you are looking for does not exist.',
          url: `/blog/${slug}`,
          noindex: true,
        },
  );

  if (!post) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950">
        <LandingHeader />
        <main className="pt-32 pb-24 sm:pt-40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Post not found
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              The blog post you are looking for does not exist.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center px-5 py-2.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              Browse all posts
            </Link>
          </div>
        </main>
        <LandingFooter />
      </div>
    );
  }

  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <LandingHeader />

      {/* Structured Data */}
      <JsonLd data={blogPostingJsonLd(post)} />
      <JsonLd
        data={breadcrumbJsonLd([
          { name: 'Home', url: 'https://logpulse.io' },
          { name: 'Blog', url: 'https://logpulse.io/blog' },
          { name: post.title, url: `https://logpulse.io/blog/${post.slug}` },
        ])}
      />

      <main className="pt-32 pb-24 sm:pt-40">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-400 transition-colors mb-8"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Blog
          </Link>

          {/* Category Badge */}
          <span
            className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium mb-4 ${categoryColors[post.category]}`}
          >
            {post.category}
          </span>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
            {post.title}
          </h1>

          {/* Author Row + Share */}
          <div className="flex items-center justify-between gap-4 mb-10 pb-8 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <AuthorAvatar name={post.author} />
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {post.author}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-500">
                  {formatBlogDate(post.date)} &middot; {post.readTime} read
                </span>
              </div>
            </div>
            <ShareButtons title={post.title} slug={post.slug} />
          </div>

          {/* Article Content */}
          <div className="prose-custom">
            {post.content.map((section, i) => (
              <ArticleSection key={i} section={section} />
            ))}
          </div>

          {/* Bottom share bar */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enjoyed this article? Share it with your network.
            </p>
            <ShareButtons title={post.title} slug={post.slug} />
          </div>
        </article>

        {/* Read More */}
        {otherPosts.length > 0 && (
          <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 pt-12 border-t border-gray-200 dark:border-gray-800">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Read more
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {otherPosts.map((related) => (
                <Link
                  key={related.slug}
                  to={`/blog/${related.slug}`}
                  className="group block rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 p-4 hover:border-primary-500/50 dark:hover:border-primary-500/40 transition-colors"
                >
                  <span
                    className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mb-3 ${categoryColors[related.category]}`}
                  >
                    {related.category}
                  </span>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1 group-hover:text-primary-500 transition-colors line-clamp-2">
                    {related.title}
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-500">
                    {related.readTime} read
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <LandingFooter />
    </div>
  );
}
